import React, { useState, useEffect, useCallback, useRef } from 'react';
import { HardDrive, Pencil, Trash2, Save, X, RefreshCw, ChevronLeft, ChevronRight, Search, GripVertical, Play, Wrench, Archive } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../../context/NotificationContext';
import { dbBackupApi } from '../../services/api';
import StuiSelect from '../../components/common/StuiSelect';
import { formatTimestamp } from '../../utils/dateTime';

const FREQUENCIES = ['Daily', 'Weekly'];
const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const IN_FLIGHT = ['running', 'copying', 'exporting', 'cleaning'];

/**
 * Administration > Database > Database Backups
 *
 * Per-database BACPAC schedules (DevTrack 125). The list is driven by the set of
 * backup-able databases rather than by existing jobs, so an UNSCHEDULED database is
 * visible and obviously unscheduled instead of simply absent.
 *
 * SysAdmin only (route-guarded in App.js, nav flagged sysAdminOnly).
 *
 * STUI-only: reuses the class vocabulary established by TableOwnershipPage /
 * DatabasesPage. No new STUI classes, no inline CSS.
 */
const DatabaseBackupsPage = () => {
  const [targets, setTargets] = useState([]);
  const [selectedName, setSelectedName] = useState(null);
  const [editing, setEditing] = useState(null);
  const [dirtyFields, setDirtyFields] = useState(new Set());
  const [runs, setRuns] = useState([]);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [listCollapsed, setListCollapsed] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const { notify } = useNotifications();
  const navigate = useNavigate();

  useEffect(() => {
    const handleClickOutside = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await dbBackupApi.getJobs();
      setTargets(res.data || []);
    } catch (err) {
      notify.error(err.response?.data?.error || 'Failed to load backup schedules');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const selected = targets.find(t => t.databaseName === selectedName) || null;

  const loadRuns = useCallback(async (dbName) => {
    if (!dbName) { setRuns([]); return; }
    try {
      const res = await dbBackupApi.getRuns({ dbName, limit: 15 });
      setRuns(res.data || []);
    } catch {
      setRuns([]);
    }
  }, []);

  useEffect(() => { loadRuns(selectedName); }, [selectedName, loadRuns]);

  // An export takes 4-5 minutes even on a tiny database, so the only sane way to show
  // progress is to poll — and only while something is actually in flight.
  const busy = !!status?.busy || IN_FLIGHT.includes(status?.run?.status);
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const res = await dbBackupApi.getStatus();
        if (!cancelled) setStatus(res.data);
      } catch { /* transient — next tick retries */ }
    };
    tick();
    const interval = setInterval(tick, busy ? 4000 : 20000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [busy]);

  // When a run finishes, refresh the schedule list and history so Last Run is truthful.
  const prevBusy = useRef(false);
  useEffect(() => {
    if (prevBusy.current && !busy) {
      load();
      loadRuns(selectedName);
    }
    prevBusy.current = busy;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busy]);

  const blankForm = (target) => ({
    isEnabled: true,
    frequency: 'Daily',
    dayOfWeek: null,
    runAtUtc: '02:00',
    retentionDays: 90,
    databaseName: target.databaseName,
    databaseSlug: target.databaseSlug,
    isNew: true
  });

  const formFromJob = (target) => ({
    isEnabled: target.job.isEnabled,
    frequency: target.job.frequency,
    dayOfWeek: target.job.dayOfWeek,
    runAtUtc: target.job.runAtUtc,
    retentionDays: target.job.retentionDays,
    databaseName: target.databaseName,
    databaseSlug: target.databaseSlug,
    isNew: false
  });

  const startEdit = () => {
    if (!selected) return;
    setEditing(selected.job ? formFromJob(selected) : blankForm(selected));
    setDirtyFields(selected.job ? new Set() : new Set(['runAtUtc']));
  };

  const cancelEdit = () => {
    if (dirtyFields.size > 0) {
      notify.confirm('Discard unsaved changes?', () => { setEditing(null); setDirtyFields(new Set()); });
      return;
    }
    setEditing(null);
    setDirtyFields(new Set());
  };

  const handleField = (field, value) => {
    setEditing(prev => {
      const next = { ...prev, [field]: value };
      // A Weekly schedule needs a day; a Daily one must not carry a stale day.
      if (field === 'frequency') next.dayOfWeek = value === 'Weekly' ? (prev.dayOfWeek ?? 0) : null;
      return next;
    });
    const orig = selected?.job ? formFromJob(selected) : blankForm(selected || {});
    const newDirty = new Set(dirtyFields);
    if (value !== orig[field]) newDirty.add(field); else newDirty.delete(field);
    setDirtyFields(newDirty);
  };

  const handleSave = async () => {
    if (!editing || dirtyFields.size === 0) return;
    if (!/^\d{1,2}:\d{2}$/.test(editing.runAtUtc || '')) {
      notify.error('Run At must be HH:mm (UTC)');
      return;
    }
    if (!(editing.retentionDays >= 1)) {
      notify.error('Retention must be at least 1 day');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        databaseName: editing.databaseName,
        databaseSlug: editing.databaseSlug,
        isEnabled: editing.isEnabled,
        frequency: editing.frequency,
        dayOfWeek: editing.frequency === 'Weekly' ? Number(editing.dayOfWeek ?? 0) : null,
        runAtUtc: editing.runAtUtc,
        retentionDays: Number(editing.retentionDays)
      };
      if (editing.isNew) await dbBackupApi.createJob(payload);
      else await dbBackupApi.updateJob(selected.job.id, payload);

      setEditing(null);
      setDirtyFields(new Set());
      await load();
      notify.success(`Backup schedule saved for ${editing.databaseName}`);
    } catch (err) {
      notify.error(err.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = () => {
    if (!selected?.job) return;
    notify.confirm(
      `Remove the backup schedule for '${selected.databaseName}'? Existing backups and run history are kept.`,
      async () => {
        try {
          await dbBackupApi.deleteJob(selected.job.id);
          setEditing(null);
          setDirtyFields(new Set());
          await load();
        } catch (err) {
          notify.error(err.response?.data?.error || 'Delete failed');
        }
      }
    );
  };

  const handleRunNow = async () => {
    if (!selected?.job) return;
    try {
      await dbBackupApi.runNow(selected.job.id);
      notify.success(`Backup started for ${selected.databaseName} — this takes several minutes`);
      const res = await dbBackupApi.getStatus();
      setStatus(res.data);
    } catch (err) {
      notify.error(err.response?.data?.error || 'Could not start backup');
    }
  };

  const handleReconcile = async () => {
    try {
      const res = await dbBackupApi.reconcile();
      const { staleRuns, orphanDbs } = res.data || {};
      if (!staleRuns && !orphanDbs) notify.success('Nothing to reconcile — no stale runs or orphaned copies');
      else notify.warning(`Reconciled: ${staleRuns} stale run(s), ${orphanDbs} orphaned copy(ies) dropped`);
      await load();
      await loadRuns(selectedName);
    } catch (err) {
      notify.error(err.response?.data?.error || 'Reconcile failed');
    }
  };

  const statusClass = (s) => {
    if (s === 'succeeded') return 'stui-status-complete';
    if (s === 'failed') return 'stui-status-failed';
    if (IN_FLIGHT.includes(s)) return 'stui-status-active';
    return '';
  };

  const fmtStamp = (iso) => formatTimestamp(iso, { showZone: true }) || '—';

  // runAtUtc is a UTC time of day; today's UTC date anchors it to an instant so the person sees
  // it on their own clock, with the zone.
  const fmtLocalTime = (hhmm) => {
    if (!/^\d{1,2}:\d{2}$/.test(hhmm || '')) return null;
    const [h, m] = hhmm.split(':');
    const day = new Date().toISOString().slice(0, 10);
    return formatTimestamp(`${day}T${h.padStart(2, '0')}:${m}:00Z`, { mode: 'time', showZone: true }) || null;
  };

  const fmtSize = (bytes) => {
    if (bytes == null) return '—';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1073741824) return `${(bytes / 1048576).toFixed(1)} MB`;
    return `${(bytes / 1073741824).toFixed(2)} GB`;
  };

  const fmtDuration = (sec) => {
    if (sec == null) return '—';
    if (sec < 60) return `${sec}s`;
    return `${Math.floor(sec / 60)}m ${sec % 60}s`;
  };

  const scheduleSummary = (t) => {
    if (!t.job) return 'not scheduled';
    const when = t.job.frequency === 'Weekly'
      ? `${DAY_NAMES[t.job.dayOfWeek ?? 0].slice(0, 3)} ${t.job.runAtUtc}Z`
      : `Daily ${t.job.runAtUtc}Z`;
    return `${when} · ${t.job.retentionDays}d${t.job.isEnabled ? '' : ' · disabled'}`;
  };

  const isEditing = !!editing;
  const hasDirty = dirtyFields.size > 0;
  const scheduledCount = targets.filter(t => t.job && t.job.isEnabled).length;

  const filtered = searchTerm.trim()
    ? targets.filter(t => t.databaseName.toLowerCase().includes(searchTerm.trim().toLowerCase()))
    : targets;

  if (loading) return <div className="stui-detail-empty"><HardDrive size={48} /><p>Loading...</p></div>;

  return (
    <div className="stui-module-container">
      <div className="stui-module-header">
        <div className="stui-module-header-left">
          <HardDrive size={20} />
          <h1>Database Backups</h1>
          <span className="stui-module-header-meta">
            {targets.length} databases · {scheduledCount} scheduled
            {busy && status?.run ? ` · ${status.run.status} ${status.run.databaseName}` : ''}
          </span>
        </div>
        <div className="stui-module-header-actions">
          {isEditing && !editing.isNew ? (
            <button className="stui-crud-btn stui-delete-btn" onClick={handleDelete} title="Remove schedule">
              <Trash2 size={18} />
            </button>
          ) : null}
          <button
            className={`stui-crud-btn ${!isEditing && !selected ? 'disabled' : ''}`}
            onClick={isEditing ? cancelEdit : startEdit}
            disabled={!selected && !isEditing}
            title={isEditing ? 'Cancel' : 'Edit schedule'}
          >
            {isEditing ? <X size={18} /> : <Pencil size={18} />}
          </button>
          <button
            className={`stui-crud-btn ${!hasDirty ? 'disabled' : ''}`}
            onClick={handleSave}
            disabled={!hasDirty || saving}
            title="Save"
          >
            <Save size={18} />
          </button>
          <div className="stui-action-menu" ref={menuRef}>
            <button className="stui-action-menu-btn" onClick={() => setMenuOpen(!menuOpen)} title="Actions">
              <GripVertical size={16} />
            </button>
            {menuOpen && (
              <div className="stui-action-menu-dropdown">
                <button className="stui-action-menu-item" onClick={() => { setMenuOpen(false); load(); loadRuns(selectedName); }}>
                  <RefreshCw size={16} /><span>Refresh</span>
                </button>
                <button className="stui-action-menu-item" onClick={() => { setMenuOpen(false); handleReconcile(); }}>
                  <Wrench size={16} /><span>Reconcile Orphans</span>
                </button>
                <button className="stui-action-menu-item" onClick={() => { setMenuOpen(false); navigate('/admin/backup-browser'); }}>
                  <Archive size={16} /><span>Backup Browser</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="stui-list-view">
        {listCollapsed ? (
          <div className="stui-list-panel stui-collapsed">
            <button onClick={() => setListCollapsed(false)} className="stui-list-panel-expand-btn" title="Expand">
              <ChevronRight size={16} />
            </button>
          </div>
        ) : (
          <div className="stui-list-panel">
            <div className="stui-list-panel-header">
              <button onClick={() => setListCollapsed(true)} className="stui-list-panel-collapse-btn" title="Collapse">
                <ChevronLeft size={16} />
              </button>
              <div className="stui-search-wrapper">
                <Search size={14} className="stui-search-icon" />
                <input
                  className="stui-field-input"
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  placeholder="Search databases..."
                />
              </div>
            </div>
            <div className="stui-list-scroll">
              {filtered.length === 0 ? (
                <div className="stui-list-empty">{searchTerm ? 'No matches.' : 'No databases found.'}</div>
              ) : (
                filtered.map(t => (
                  <button
                    key={t.databaseName}
                    onClick={() => { setSelectedName(t.databaseName); setEditing(null); setDirtyFields(new Set()); }}
                    className={`stui-list-item ${selectedName === t.databaseName ? 'selected' : ''}`}
                  >
                    <span className="stui-list-item-primary">
                      {t.databaseName}
                      {t.job?.lastStatus ? <span className={statusClass(t.job.lastStatus)}> ●</span> : null}
                    </span>
                    <span className="stui-list-item-secondary">{scheduleSummary(t)}</span>
                  </button>
                ))
              )}
            </div>
          </div>
        )}

        <div className="stui-detail-panel">
          {editing ? (
            <div className="stui-detail-content">
              <div className="stui-detail-grid">
                <div className="stui-col-24">
                  <div className="stui-detail-section-header">
                    {editing.isNew ? `New Schedule — ${editing.databaseName}` : `Edit Schedule — ${editing.databaseName}`}
                  </div>
                </div>
                <div className="stui-field-group stui-col-8">
                  <label>Enabled</label>
                  <StuiSelect
                    className="stui-field-input"
                    value={editing.isEnabled ? 'yes' : 'no'}
                    onChange={e => handleField('isEnabled', e.target.value === 'yes')}
                  >
                    <option value="yes">Enabled</option>
                    <option value="no">Disabled</option>
                  </StuiSelect>
                </div>
                <div className="stui-field-group stui-col-8">
                  <label>Frequency</label>
                  <StuiSelect
                    className="stui-field-input"
                    value={editing.frequency}
                    onChange={e => handleField('frequency', e.target.value)}
                  >
                    {FREQUENCIES.map(f => <option key={f} value={f}>{f}</option>)}
                  </StuiSelect>
                </div>
                <div className="stui-field-group stui-col-8">
                  <label>Day of Week</label>
                  <StuiSelect
                    className="stui-field-input"
                    value={editing.dayOfWeek ?? ''}
                    onChange={e => handleField('dayOfWeek', e.target.value === '' ? null : Number(e.target.value))}
                    disabled={editing.frequency !== 'Weekly'}
                  >
                    <option value="">—</option>
                    {DAY_NAMES.map((d, i) => <option key={d} value={i}>{d}</option>)}
                  </StuiSelect>
                </div>
                <div className="stui-field-group stui-col-8">
                  <label>Run At (UTC)</label>
                  <input
                    className="stui-field-input"
                    value={editing.runAtUtc}
                    onChange={e => handleField('runAtUtc', e.target.value)}
                    placeholder="02:00"
                  />
                  {fmtLocalTime(editing.runAtUtc) && (
                    <span className="stui-hint">{fmtLocalTime(editing.runAtUtc)}</span>
                  )}
                </div>
                <div className="stui-field-group stui-col-8">
                  <label>Retention (days)</label>
                  <input
                    className="stui-field-input"
                    type="number"
                    min="1"
                    value={editing.retentionDays}
                    onChange={e => handleField('retentionDays', e.target.value)}
                  />
                  <span className="stui-hint">Long-term retention is off, so this is the only copy past 7 days</span>
                </div>
              </div>
            </div>
          ) : selected ? (
            <div className="stui-detail-content">
              <div className="stui-detail-grid">
                <div className="stui-col-24">
                  <div className="stui-detail-section-header">
                    {selected.databaseName}
                    {selected.job?.lastStatus ? (
                      <span className={statusClass(selected.job.lastStatus)}> ● {selected.job.lastStatus}</span>
                    ) : null}
                  </div>
                </div>

                {busy && status?.run?.databaseName === selected.databaseName && (
                  <div className="stui-col-24">
                    <div className="stui-hint">
                      <RefreshCw size={14} className="stui-spinning" /> {status.run.currentStep || status.run.status}
                    </div>
                  </div>
                )}

                {!selected.job ? (
                  <div className="stui-col-24">
                    <div className="stui-list-empty">
                      No backup schedule for this database. Click the pencil to create one.
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="stui-field-group stui-col-8">
                      <label>Schedule</label>
                      <input className="stui-field-input" value={scheduleSummary(selected)} readOnly />
                    </div>
                    <div className="stui-field-group stui-col-8">
                      <label>Next Run</label>
                      <input className="stui-field-input" value={fmtStamp(selected.job.nextRunUtc)} readOnly />
                    </div>
                    <div className="stui-field-group stui-col-8">
                      <label>Last Run</label>
                      <input className="stui-field-input" value={fmtStamp(selected.job.lastRunUtc)} readOnly />
                    </div>

                    <div className="stui-col-24">
                      <div className="stui-detail-section-header">Run History</div>
                    </div>
                    <div className="stui-col-24">
                      {runs.length === 0 ? (
                        <div className="stui-inner-table-empty">No runs recorded yet.</div>
                      ) : (
                        <table className="stui-inner-table">
                          <thead>
                            <tr>
                              <th>Started</th>
                              <th>Status</th>
                              <th>Duration</th>
                              <th>Size</th>
                              <th>Reaped</th>
                              <th>Trigger</th>
                            </tr>
                          </thead>
                          <tbody>
                            {runs.map(r => (
                              <React.Fragment key={r.id}>
                                <tr>
                                  <td className="stui-inner-table-cell-mono">{fmtStamp(r.startedAt)}</td>
                                  <td><span className={statusClass(r.status)}>● {r.status}</span></td>
                                  <td>{fmtDuration(r.durationSec)}</td>
                                  <td>{fmtSize(r.sizeBytes)}</td>
                                  <td>{r.blobsReaped ?? '—'}</td>
                                  <td>{r.triggerSource}</td>
                                </tr>
                                {r.errorMessage && (
                                  <tr>
                                    <td colSpan={6} className="stui-td-muted">{r.errorMessage}</td>
                                  </tr>
                                )}
                              </React.Fragment>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>

                    <div className="stui-col-24">
                      <div className="stui-btn-row">
                        <button
                          className="stui-btn stui-btn-primary"
                          onClick={handleRunNow}
                          disabled={busy || !selected.job}
                        >
                          <Play size={14} /> {busy ? 'Backup in progress...' : 'Run Now'}
                        </button>
                        <button className="stui-btn stui-btn-secondary" onClick={() => navigate('/admin/backup-browser')}>
                          <Archive size={14} /> View Backups
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          ) : (
            <div className="stui-detail-empty">
              <HardDrive size={48} />
              <p>Select a database to view or create its backup schedule.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DatabaseBackupsPage;
